// src/features/task/formProperties.ts
// What a task form asks for, decided by the task type rather than by this file.
//
// The property panel used to be a hardcoded list of nine rows, identical for
// every type, which meant a chore still had to be told it has no due date and
// nobody could put the assignee above the priority. `TaskTypeDefinition.
// form_properties` is the type's answer to both questions, and this module is
// the one place the frontend reads it.
//
// The catalogue itself is never written down here. It is derived from the
// generated `TaskFormPropertyValues`, so adding a property to the proto adds it
// to the default set on both sides at once — the whole reason the backend chose
// an enum over free-form string keys. A hand-kept copy would drift, and the day
// it drifted a property would simply stop appearing with nothing failing.

import { TaskFormPropertyValues } from '@core/types/task';
import type { TaskFormProperty, TaskTypeDefinition, TaskTypeProperty } from '@core/types/task';

/**
 * The platform default set: the whole catalogue, in declaration order.
 *
 * This is what a type with no `form_properties` gets — the same list the forms
 * showed before types could say otherwise, so nothing had to be migrated.
 * UNSPECIFIED is the proto zero value, not a property, so it never appears.
 */
export const FORM_PROPERTY_CATALOGUE: readonly TaskFormProperty[] = TaskFormPropertyValues.filter(
  (value) => value !== 'TASK_FORM_PROPERTY_UNSPECIFIED',
);

/** One property a form should offer, resolved from the type's configuration. */
export interface FormPropertySlot {
  /**
   * Stable identity across renders and reorders: the enum value for a built-in
   * property, `cf:<id>` for a custom field. Built-in and custom ids live in
   * different namespaces on the wire, so they need one here too.
   */
  key: string;
  /** The built-in property, or null when this slot names a custom field. */
  property: TaskFormProperty | null;
  /** CustomFieldDefinition id or slug; empty for a built-in property. */
  customFieldId: string;
  /** The form must not be submittable while this property is unanswered. */
  required: boolean;
}

/** The resolved shape of a type's form: what to show, and what it left out. */
export interface FormLayout {
  /** Shown, in the order the type asked for. */
  slots: FormPropertySlot[];
  /**
   * Configured and switched off. Kept because a value already typed into one of
   * these must not be sent without saying so — the row is gone, the value is
   * not, and a form that quietly drops what it collected is worse than one that
   * refuses to.
   */
  hidden: FormPropertySlot[];
  /** False when the type says nothing and the platform default set applies. */
  configured: boolean;
}

/** `cf:` keeps custom field ids from colliding with catalogue value names. */
export function customFieldSlotKey(fieldId: string): string {
  return `cf:${fieldId}`;
}

/** The key a stored row occupies — the same one `FormPropertySlot.key` uses. */
export function formPropertyRowKey(row: TaskTypeProperty): string {
  if (row.custom_field_id) return customFieldSlotKey(row.custom_field_id);
  return row.property || '';
}

function slotForRow(row: TaskTypeProperty): FormPropertySlot | null {
  const key = formPropertyRowKey(row);
  if (!key || key === 'TASK_FORM_PROPERTY_UNSPECIFIED') return null;
  return {
    key,
    property: row.custom_field_id ? null : ((row.property ?? null) as TaskFormProperty | null),
    customFieldId: row.custom_field_id || '',
    // A hidden row's `required` cannot be honoured: nothing on screen could
    // clear it. The service refuses to store the pair, so this only ever
    // guards against a record written before it did.
    required: Boolean(row.required) && !row.hidden,
  };
}

/** The type row, which every form keeps whatever the configuration says. */
const TYPE_SLOT: FormPropertySlot = {
  key: 'TASK_FORM_PROPERTY_TYPE',
  property: 'TASK_FORM_PROPERTY_TYPE',
  customFieldId: '',
  required: false,
};

/**
 * Read a type's form configuration.
 *
 * Empty means the platform default set, which is why this returns the whole
 * catalogue rather than nothing: `form_properties` is a statement about the
 * form, and no statement is not the same as "ask for nothing".
 *
 * The type row survives being switched off. It is the control that changes
 * which configuration applies, so hiding it would strand the person on whatever
 * type the form opened with, with no way back — including no way back to a type
 * whose form is complete. Everything else is the administrator's to remove.
 */
export function resolveFormLayout(def: TaskTypeDefinition | null | undefined): FormLayout {
  const rows = def?.form_properties ?? [];

  if (rows.length === 0) {
    return {
      slots: FORM_PROPERTY_CATALOGUE.map((property) => ({
        key: property,
        property,
        customFieldId: '',
        required: false,
      })),
      hidden: [],
      configured: false,
    };
  }

  const slots: FormPropertySlot[] = [];
  const hidden: FormPropertySlot[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const slot = slotForRow(row);
    // A duplicate is the service's error to refuse; here the first mention
    // simply wins, so a bad record renders instead of throwing.
    if (!slot || seen.has(slot.key)) continue;
    seen.add(slot.key);
    if (row.hidden) hidden.push(slot);
    else slots.push(slot);
  }

  if (!seen.has(TYPE_SLOT.key)) slots.unshift(TYPE_SLOT);
  else if (!slots.some((slot) => slot.key === TYPE_SLOT.key)) {
    // Configured as hidden: put it back where it was asked for, so an
    // administrator who switches it on again finds it in the same place.
    const index = rows.findIndex((row) => formPropertyRowKey(row) === TYPE_SLOT.key);
    const before = rows
      .slice(0, index)
      .map(formPropertyRowKey)
      .filter((key) => slots.some((slot) => slot.key === key)).length;
    slots.splice(before, 0, TYPE_SLOT);
  }

  return { slots, hidden: hidden.filter((slot) => slot.key !== TYPE_SLOT.key), configured: true };
}

/**
 * i18n key naming a built-in property, reusing the labels the create form
 * already shows. The panel and the settings editor have to call the same
 * property the same thing, or the configuration reads as being about something
 * else than what it changes.
 */
const PROPERTY_LABEL_KEYS: Record<TaskFormProperty, string> = {
  TASK_FORM_PROPERTY_UNSPECIFIED: '',
  TASK_FORM_PROPERTY_PARENT: 'task.createForm.pill.parent',
  TASK_FORM_PROPERTY_TYPE: 'task.createForm.pill.type',
  TASK_FORM_PROPERTY_CATEGORY: 'task.createForm.pill.category',
  TASK_FORM_PROPERTY_PRIORITY: 'task.createForm.pill.priority',
  TASK_FORM_PROPERTY_ASSIGNEE: 'task.createForm.pill.assignee',
  TASK_FORM_PROPERTY_DUE_DATE: 'task.field.dueDate',
  TASK_FORM_PROPERTY_START_DATE: 'task.startDate',
  TASK_FORM_PROPERTY_ESTIMATE: 'task.createForm.pill.estimate',
  TASK_FORM_PROPERTY_TAGS: 'task.createForm.pill.tags',
  TASK_FORM_PROPERTY_WATCHERS: 'task.watchers',
  TASK_FORM_PROPERTY_PROGRESS: 'task.progress',
};

export function formPropertyLabelKey(property: TaskFormProperty): string {
  return PROPERTY_LABEL_KEYS[property] || property;
}

/**
 * The field name a property's validation message is keyed by, matching the
 * names the create form already uses for the ones it validated before.
 */
const PROPERTY_ERROR_FIELDS: Record<TaskFormProperty, string> = {
  TASK_FORM_PROPERTY_UNSPECIFIED: '',
  TASK_FORM_PROPERTY_PARENT: 'parent',
  TASK_FORM_PROPERTY_TYPE: 'type',
  TASK_FORM_PROPERTY_CATEGORY: 'category',
  TASK_FORM_PROPERTY_PRIORITY: 'priority',
  TASK_FORM_PROPERTY_ASSIGNEE: 'assignee',
  TASK_FORM_PROPERTY_DUE_DATE: 'due_date',
  TASK_FORM_PROPERTY_START_DATE: 'start_date',
  TASK_FORM_PROPERTY_ESTIMATE: 'estimated_hours',
  TASK_FORM_PROPERTY_TAGS: 'tags',
  TASK_FORM_PROPERTY_WATCHERS: 'watchers',
  TASK_FORM_PROPERTY_PROGRESS: 'progress',
};

/** Error key for a slot — the custom field's own id when it names one. */
export function slotErrorField(slot: FormPropertySlot): string {
  if (slot.customFieldId) return slot.customFieldId;
  return slot.property ? PROPERTY_ERROR_FIELDS[slot.property] : '';
}

/**
 * The properties a *creation* form can actually carry.
 *
 * `CreateTaskRequest` has no progress field — progress is something a task
 * accumulates, not something it is filed with — so a progress row on the create
 * form would collect a number nothing would save. The catalogue is about task
 * forms in general and rightly still offers it; the create surfaces skip it,
 * and the settings editor says so on the row rather than letting an
 * administrator configure a control that will never appear.
 */
export const CREATE_FORM_PROPERTIES: ReadonlySet<TaskFormProperty> = new Set(
  FORM_PROPERTY_CATALOGUE.filter((property) => property !== 'TASK_FORM_PROPERTY_PROGRESS'),
);

/** True when the create surfaces have a control for this slot. */
export function slotRendersOnCreate(slot: FormPropertySlot): boolean {
  if (slot.customFieldId) return true;
  return !!slot.property && CREATE_FORM_PROPERTIES.has(slot.property);
}

// ---------------------------------------------------------------------------
// Editing a configuration
// ---------------------------------------------------------------------------

/** The whole catalogue as stored rows — what "start from the default" writes. */
export function catalogueAsRows(): TaskTypeProperty[] {
  return FORM_PROPERTY_CATALOGUE.map((property) => ({
    property,
    custom_field_id: '',
    required: false,
    hidden: false,
  }));
}

/** Move a row within the list, clamped rather than wrapped. */
export function moveFormProperty(
  rows: TaskTypeProperty[],
  from: number,
  to: number,
): TaskTypeProperty[] {
  if (from === to || from < 0 || from >= rows.length) return rows;
  const target = Math.max(0, Math.min(rows.length - 1, to));
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved);
  return next;
}

/**
 * Apply one edit to a row, keeping the pair the service refuses out of reach:
 * hiding a property clears its required flag rather than letting the two be set
 * together and answered by a 400 after the save.
 */
export function updateFormProperty(
  rows: TaskTypeProperty[],
  index: number,
  patch: Partial<Pick<TaskTypeProperty, 'required' | 'hidden'>>,
): TaskTypeProperty[] {
  return rows.map((row, i) => {
    if (i !== index) return row;
    const next = { ...row, ...patch };
    if (next.hidden) next.required = false;
    return next;
  });
}
